import * as Yup from 'yup';

import { ALLOWED_CHARACTERS_REGEX, IMO_NUMBER_REGEX, MAX_FILE_SIZE_30MB } from '@/common/constants';
import { differenceInYears } from 'date-fns';
import { parseCurrency } from '@/utils/currency';

const hasFileValue = (value: unknown) => {
    if (Array.isArray(value)) {
        return value.length > 0;
    }

    if (typeof value === 'string') {
        return value.trim().length > 0;
    }

    return value !== null && value !== undefined;
};

const ALLOWED_SIRE_REVIEW_FILE_TYPES = [
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
];

const isFileTooLarge = (value: unknown) => {
    if (!hasFileValue(value) || typeof value === 'string') {
        return false;
    }

    if (Array.isArray(value)) {
        return value.some((file) => file instanceof File && file.size > MAX_FILE_SIZE_30MB);
    }

    return value instanceof File ? value.size > MAX_FILE_SIZE_30MB : false;
};

const hasAllowedFileType = (value: unknown) => {
    if (!hasFileValue(value) || typeof value === 'string') {
        return true;
    }

    const isAllowedFile = (file: unknown) =>
        file instanceof File && (
            ALLOWED_SIRE_REVIEW_FILE_TYPES.includes(file.type)
            || /\.(pdf|xls|xlsx)$/i.test(file.name)
        );

    if (Array.isArray(value)) {
        return value.every((file) => isAllowedFile(file));
    }

    return isAllowedFile(value);
};

export const addSIRESchema = (maxShipAgeYears: number, maxDWT: number) => {
    return (
        Yup.object({
            requestor: Yup.string()
                .matches(ALLOWED_CHARACTERS_REGEX, 'Input must only contain alphabets, numbers, commas, dots, slashes, and dashes.')
                .required('Requestor is required'),
            shipName: Yup.string()
                .required('Ship name is required'),
            pic: Yup.string()
                .required('PIC is required'),
            imoNumber: Yup.string()
                .matches(IMO_NUMBER_REGEX, 'IMO / Registration number must contain only alphabets, digits, dots, slashes, and dashes.')
                .required('IMO number is required'),
            reviewDate: Yup.date()
                .required('Review date is required'),
            shipBuildDate: Yup.date()
                .required('Vessel build date is required'),


            dwt: Yup.string()
                .required("Vessel DWT is required")
                .test('value', 'DWT must be higher than 0', (value) => (parseCurrency(value) > 0)),
            addAld: Yup.string()
                .required('ADD ALD is required'),
            classSurveyStatus: Yup.mixed()
                .test('required-file', 'Class survey status file is required', (value) => hasFileValue(value))
                .test('fileType', 'Only PDF, XLS, or XLSX files are allowed', (value) => hasAllowedFileType(value))
                .test('fileSize', 'File size is too large', (value) => !isFileTooLarge(value)),
            classSurveyStatusDate: Yup.date()
                .required('Class survey status date is required'),
            capSurveyReport: Yup.mixed()
                .test('fileType', 'Only PDF, XLS, or XLSX files are allowed', (value) => hasAllowedFileType(value))
                .test('fileSize', 'File size is too large', (value) => !isFileTooLarge(value))
                .when(['shipBuildDate', 'dwt'], {
                    is: (shipBuildDate: Date, dwt: string) => (
                        differenceInYears(new Date(), shipBuildDate) > maxShipAgeYears
                        && parseCurrency(dwt) > maxDWT
                    ),
                    then: (schema) => schema.test(
                        'required-cap-file',
                        `CAP/CAS survey report file is required for vessels older than ${maxShipAgeYears} years and DWT above ${maxDWT} t.`,
                        (value) => hasFileValue(value)
                    ),
                    otherwise: (schema) => schema.notRequired(),
                }),
            capSurveyReportDate: Yup.date().when(['capSurveyReport'], {
                is: (capSurveyReport: unknown) => hasFileValue(capSurveyReport),
                then: (schema) => schema.required('CAP/CAS survey report date is required if file is uploaded'),
                otherwise: (schema) => schema.notRequired()
            }),
            certificates: Yup.mixed()
                .nullable()
                .test('fileType', 'Only PDF, XLS, or XLSX files are allowed', (value) => hasAllowedFileType(value))
                .test('fileSize', 'File size is too large', (value) => !isFileTooLarge(value)),
            certificatesDate: Yup.date().when(['certificates'], {
                is: (certificates: unknown) => hasFileValue(certificates),
                then: (schema) => schema.required('All statutory certificates date is required if file is uploaded'),
                otherwise: (schema) => schema.notRequired()
            }),
            other: Yup.mixed()
                .nullable()
                .test('fileType', 'Only PDF, XLS, or XLSX files are allowed', (value) => hasAllowedFileType(value))
                .test('fileSize', 'File size is too large', (value) => !isFileTooLarge(value)),
            otherDate: Yup.date().when(['other'], {
                is: (other: unknown) => hasFileValue(other),
                then: (schema) => schema.required('Other file date is required if file is uploaded'),
                otherwise: (schema) => schema.notRequired()
            }),
        }));
}
